import { gql } from '@apollo/client';
import defaultFarmFields from './constants/defaultFarmFields';
import { FarmColumnsFragment } from './constants/fragments';

export const CurrentUserQuery = gql`
  query CurrentUser {
    me {
      id
      firstName
      lastName
      email
      superadminRole
      encryptedUserId
      hasValidSubscription
      updatedAt
      defaultUnitSystem
    }
  }
`;

export const FarmQuery = gql`
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      name
      address
      startYear
      mapCenter
      updatedAt
      defaultBaseMap
      baseLayerRoadsEnabled
      baseLayerDarkEnabled
      baseLayerContoursEnabled
    }
  }
`;

export const FarmActionsQuery = gql`
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      allowedActions {
        canAccess
        canEdit
        canShare
        canArchive
        canLock
        canDelete
      }
    }
  }
`;

export const FarmSubscriptionQuery = gql`
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      hasValidSubscription
      isArchived
      isLocked
    }
  }
`;

export const FarmHasSubsidiesQuery = gql`
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      hasSubsidies
    }
  }
`;

export const FarmsQuery = gql`
  query Farms {
    farms {
      id
      createdAt
      updatedAt
      objectUpdatedAt
      team {
        id
        name
      }
      ${defaultFarmFields.join(' ')}
    }
  }
`;

export const UsersQuery = gql`
  query Users {
    users {
      id
      email
      firstName
      lastName
      defaultUnitSystem
      superadminRole
      hasValidSubscription
      createdAt
      updatedAt
    }
  }
`;

export const MyProjectsQuery = gql`
  ${FarmColumnsFragment}
  query MyProjects {
    ownedFarms {
      ...FarmColumnFields
    }
  }
`;

export const MyTeamsQuery = gql`
  query MyTeamsQuery {
    me {
      id
      teams {
        id
        name
        noSubscriptionNeeded
        hasSubscription
        hasValidSubscription
        allowedActions {
          id
          canEdit
          canUpdateSettings
        }
      }
    }
  }
`;

export const TeamsQuery = gql`
  query Teams {
    teams {
      id
      name
      createdAt
      updatedAt
      hasValidSubscription
      allowedActions {
        id
        canEdit
      }
    }
  }
`;

export const TeamQuery = gql`
  query Team($id: ID!) {
    team(id: $id) {
      id
      name
      updatedAt
      hasImage
      hasSubscription
      hasValidSubscription
      imageExtension
      signedUrl
      members {
        id
        firstName
        lastName
        email
        role
        isPending
      }
      allowedActions {
        id
        canAccess
        canEdit
        canUpdateMembers
        canUpdateSettings
      }
    }
  }
`;

// Because this uses teamStripeState, use only when you want
// the stripe subscription to be updated
export const TeamRemainingPaidSeatsQuery = gql`
  query TeamRemainingPaidSeats($id: ID!) {
    teamStripeState(id: $id) {
      id
      numPaidSeats
      remainingPaidSeats
    }
  }
`;

export const CropColorFragment = gql`
  fragment CropColorFields on Color {
    id
    hexDark
    hexLight
    name
  }
`;

export const CropDisplayFragment = gql`
  ${CropColorFragment}
  fragment CropDisplayFields on Crop {
    id
    name
    color {
      ...CropColorFields
    }
  }
`;

export const MapDataQuery = gql`
  ${CropColorFragment}
  query MapData($farmId: ID!) {
    availableCrops(farmId: $farmId) {
      id
      template {
        id
        team {
          id
        }
      }
      name
      updatedAt
      description
      inRowSpacing
      betweenRowSpacing
      color {
        ...CropColorFields
      }
      cropVarieties {
        id
        name
      }
    }
    farm(farmId: $farmId) {
      id
      sections {
        id
        updatedAt
        name
        sectionCropMetaVersion
        rowOverrides
        fieldFeature
        rowFeature
        type
        headlandWidth
        headlandInnerFeature
        rotation
        rotationCenter
        offsetX
        offsetY
        rowOrderIsReversed
        orderWithinRowsIsReversed
        areaFraction
        inRowSpacing
        betweenRowSpacing
        signedUrl
        crop {
          id
          name
          color {
            ...CropColorFields
          }
        }
        primaryCropVariety {
          id
          name
        }
        primaryInRowPattern {
          id
        }
        farmId
        startYear
      }
      layers {
        id
        layerType
        displayName
        signedUrl
        width
        color
        createdAt
        updatedAt
        layerFeatures {
          id
          updatedAt
        }
      }
    }
    colors {
      ...CropColorFields
    }
  }
`;

// This is separate from MapDataQuery for use as a refetch query
export const SectionGroupsQuery = gql`
  query SectionGroups($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      sectionGroups {
        id
        farmId
        name
        sections {
          id
        }
        updatedAt
      }
    }
  }
`;

export const InRowPatternsQuery = gql`
  query InRowPatterns($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      inRowPatterns {
        id
        name
        description
        cropSequence {
          id
          crop {
            id
            name
          }
          cropVariety {
            id
            name
          }
          spacingFt
        }
        updatedAt
      }
    }
  }
`;

export const OperationsEconFragment = gql`
  fragment OperationsEconFragment on SectionsAggregate {
    cropAggregates(
      applyBuffer: $applyBuffer
      applyMargin: $applyMargin
      applySubsidy: $applySubsidy
    ) {
      crop {
        ...CropDisplayFields
      }
      operationAggregates(assignment: $assignment) {
        operation {
          id
          name
          category
          group
          timesPerYear
          equipmentDisplayName
          inputsDisplayName
          assignment
          contracted
          rented
        }
        contractOrRentCostByYear
        contractCostByYear
        rentalCostByYear
        depreciationCostByYear
        developerCostByYear
        fuelCostByYear
        inputCostByYear
        laborCostByYear
        occursDuringYearByYear
        totalCost
        totalCostByYear
        totalCostPerTree
      }
    }
  }
`;

export const YieldAggregatesEconFragment = gql`
  fragment YieldAggregateFields on YieldAggregate {
    yieldByYear
    averageYieldAtMaturity
    maxYield
    yearOfMaturity
    yield {
      id
      percentTargetYieldByYear
      targetYieldPerTree
      unit
    }
  }
`;

const DashboardFarmAggregateFragment = gql`
  fragment DashboardFarmAggregateFragment on Farm {
    id
    name
    tags
    mapCenter
    address
    startYear
    team {
      id
      name
    }
    updatedAt
    objectUpdatedAt
    owner {
      id
      firstName
      lastName
      email
    }
    sectionCount
    ecologicalSummary {
      treeCount
      acreage
    }
    cropAggregates {
      crop {
        id
        name
        color {
          id
        }
      }
      ecologicalSummary {
        treeCount
        acreage
      }
      cropVarietyAggregates {
        cropVariety {
          id
          name
        }
        treeCount
      }
    }
  }
`;

export const DasboardFarmAggregateQuery = gql`
  ${DashboardFarmAggregateFragment}
  query Farm($farmId: ID!) {
    farm(farmId: $farmId) {
      ...DashboardFarmAggregateFragment
    }
  }
`;

export const DashboardQuery = gql`
  ${DashboardFarmAggregateFragment}
  query Dashboard($id: ID!) {
    multiProjectDashboard(id: $id) {
      id
      name
      updatedAt
      cropColorMap
      farms {
        ...DashboardFarmAggregateFragment
      }
    }
  }
`;

export const OwnedDashboardsQuery = gql`
  query OwnedDashboardsQuery {
    ownedMultiProjectDashboards {
      id
      name
      updatedAt
      farmCount
    }
  }
`;

export const LayerSignedPutUrlQuery = gql`
  query LayerSignedPutUrl($farmId: ID!, $layerType: String!, $id: ID) {
    layerSignedPutUrl(farmId: $farmId, layerType: $layerType, id: $id)
  }
`;

export const NoteFragment = gql`
  fragment NoteFields on Note {
    anchorId
    anchorType
    id
    title
    body
    updatedAt
  }
`;

export const NotesQuery = gql`
  ${NoteFragment}
  query FarmNotes($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      notes {
        ...NoteFields
      }
    }
  }
`;

export const SiteInfrastructureBasisFragment = gql`
  fragment SiteInfrastructureBasisFragment on SectionsAggregate {
    capexes {
      id
      name
      baseTotalCost: totalCost(applyBuffer: false, applyMargin: false)
      bufferedTotalCost: totalCost(applyBuffer: true, applyMargin: false)
      quotedTotalCost: totalCost(applyBuffer: true, applyMargin: true)
      baseUnitCost: unitCost(applyBuffer: false, applyMargin: false)
      bufferedUnitCost: unitCost(applyBuffer: true, applyMargin: false)
      quotedUnitCost: unitCost(applyBuffer: true, applyMargin: true)
      quantity
      assignment
      displayPurchaseYear
    }
  }
`;

export const GeneticStockBasisFragment = gql`
  fragment GeneticStockBasisFragment on SectionsAggregate {
    cropAggregates {
      crop {
        id
        name
        baseUnitCost: unitCost(applyBuffer: false, applyMargin: false)
        bufferedUnitCost: unitCost(applyBuffer: true, applyMargin: false)
        quotedUnitCost: unitCost(applyBuffer: true, applyMargin: true)
      }
      ecologicalSummary {
        treeCount
        replantedTreeCount
      }
    }
  }
`;

export const OPS_BASIS_FRAGMENT_NAME = 'OperationsBasisFragment';

export const OperationsBasisFragment = gql`
  ${CropDisplayFragment}
  ${OperationsEconFragment}
  fragment OperationTotalCostByYearFragment on OperationAggregate {
    operation {
      id
    }
    totalCostByYear
  }
  fragment OperationsBasisFragment on SectionsAggregate {
    ...OperationsEconFragment
    bufferedAggregates: cropAggregates(applyBuffer: true, applyMargin: false) {
      operationAggregates(assignment: $assignment) {
        ...OperationTotalCostByYearFragment
      }
    }
    quotedAggregates: cropAggregates(applyBuffer: true, applyMargin: true) {
      operationAggregates(assignment: $assignment) {
        ...OperationTotalCostByYearFragment
      }
    }
  }
`;

export const CropInputAggregateFragment = gql`
  fragment InputAggregateFields on InputAggregate {
    input {
      id
      name
      packageSize
      packageCostWithShipping
    }
    unitQuantityByYear
    purchaseQuantityByYear
    totalCostByYear
  }
  fragment CropInputAggregateFragment on SectionsAggregate {
    cropAggregates {
      crop {
        id
        name
      }
      inputAggregates(assignment: $assignment) {
        ...InputAggregateFields
        applicationRate
      }
    }
  }
`;

export const AllVisibleTagsQuery = gql`
  query AllVisibleTags {
    allVisibleTags
  }
`;

export const TilesetsQuery = gql`
  query Tilesets {
    tilesets {
      id
      name
      type
      displayNames
      discreteKey {
        hexCode
        label
      }
      continuousKey {
        hexCode
        label
      }
      learnMoreUrl
      description
      zoomLevels
    }
  }
`;

export const AzureMapsAttributionQuery = gql`
  query AzureMapsAttribution($zoom: Int!, $bounds: [Float!]!) {
    azureMapsAttribution(zoom: $zoom, bounds: $bounds)
  }
`;
